import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogTitle,
	Label,
	type SelectOption,
} from "@exopengithub/exokit";
import { Button } from "@exopengithub/exokit/v2";
import {
	showErrorNotification,
	showSuccessNotification,
} from "molecules/notifications/notificationEvents";
import { DialogActions } from "molecules/dialog/DialogActions";
import { InputField } from "molecules/input/InputField";
import { Select } from "molecules/select/Select";
import { useCreateApiKeyMutation } from "./api/apiKeyApi";
import type { CreateApiKeyRequest } from "./apiKeyTypes";

interface CreateApiKeyDialogProps {
	open: boolean;
	onClose: () => void;
	companyDomainId: string;
	legalEntityOptions: SelectOption[];
}

// An empty selection means "not connected to any current Legal Entity", so the
// leMasterKey is omitted from the request rather than sent as an empty value.
export function buildCreateApiKeyArgs(
	companyDomainId: string,
	sourceKey: string,
	leMasterKey: string | null,
): CreateApiKeyRequest {
	return leMasterKey
		? { companyDomainId, sourceKey, leMasterKey }
		: { companyDomainId, sourceKey };
}

export function CreateApiKeyDialog({
	open,
	onClose,
	companyDomainId,
	legalEntityOptions,
}: CreateApiKeyDialogProps) {
	const { t } = useTranslation();
	const legalEntityLabelId = useId();
	const [sourceKey, setSourceKey] = useState("");
	const [leMasterKey, setLeMasterKey] = useState<string | null>(null);
	const [sourceKeyError, setSourceKeyError] = useState<string | null>(null);
	const [createApiKey, { isLoading }] = useCreateApiKeyMutation();

	function resetAndClose() {
		setSourceKey("");
		setLeMasterKey(null);
		setSourceKeyError(null);
		onClose();
	}

	async function handleCreate() {
		const trimmed = sourceKey.trim();
		if (!trimmed) {
			setSourceKeyError(t("This field is required"));
			return;
		}
		try {
			await createApiKey(
				buildCreateApiKeyArgs(companyDomainId, trimmed, leMasterKey),
			).unwrap();
			showSuccessNotification({ message: t("API key created") });
			resetAndClose();
		} catch {
			showErrorNotification({ message: t("Failed to create API key") });
		}
	}

	return (
		<Dialog open={open} onOpenChange={(isOpen) => !isOpen && resetAndClose()}>
			<DialogContent>
				<DialogTitle>{t("New API key")}</DialogTitle>
				<DialogBody className="space-y-4">
					<InputField
						label={t("Source key")}
						required
						value={sourceKey}
						onChange={(event) => {
							setSourceKey(event.target.value);
							setSourceKeyError(null);
						}}
						aria-invalid={!!sourceKeyError}
						hint={sourceKeyError ?? undefined}
					/>
					<div>
						<Label
							id={legalEntityLabelId}
							optional
							optionalLabel={t("(optional)")}
						>
							{t("Legal entity")}
						</Label>
						<Select
							aria-labelledby={legalEntityLabelId}
							placeholder={t("Select legal entity")}
							withFilter
							clearable
							options={legalEntityOptions}
							value={leMasterKey}
							onChange={setLeMasterKey}
						/>
					</div>
				</DialogBody>
				<DialogActions withCancelButton>
					<Button
						onClick={handleCreate}
						disabled={isLoading}
						isLoading={isLoading}
					>
						{t("Create API key")}
					</Button>
				</DialogActions>
			</DialogContent>
		</Dialog>
	);
}
